import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { Timer, X } from "lucide-react";
function eventDistanceMeters(eventName) {
    const normalized = eventName.trim().toLowerCase();
    if (normalized === "1 mile")
        return 1609;
    if (normalized === "2 mile")
        return 3218;
    return Number(normalized.match(/^(\d+)/)?.[1] ?? 0);
}
export function generateSplitDistances(eventName, indoor) {
    const normalized = eventName.trim().toLowerCase();
    const distance = eventDistanceMeters(eventName);
    const interval = indoor ? 200 : 400;
    if (!distance)
        return [];
    if (normalized === "1 mile" || normalized === "2 mile") {
        const nominalDistance = normalized === "1 mile" ? 1600 : 3200;
        const extraMeters = distance - nominalDistance;
        return Array.from({ length: nominalDistance / interval }, (_, index) => interval + (index === 0 ? extraMeters : 0));
    }
    const remainder = distance % interval;
    const distances = remainder ? [remainder] : [];
    while (distances.reduce((total, value) => total + value, 0) < distance)
        distances.push(interval);
    return distances;
}
export function splitTimeToSeconds(time) {
    const [whole, fraction = ""] = time.split(".");
    const parts = whole.split(":").map(Number);
    const seconds = parts.length === 2 ? parts[0] * 60 + parts[1] : parts[0];
    return seconds + (fraction ? Number(`0.${fraction}`) : 0);
}
export function formatSplitTotal(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remaining = (seconds - minutes * 60).toFixed(2).padStart(5, "0");
    return minutes > 0 ? `${minutes}:${remaining}` : String(Number(seconds.toFixed(2)));
}
function digits(value, maxLength) {
    return value.replace(/\D/g, "").slice(0, maxLength);
}
export function SplitTimeEditor({ distance, currentTime, timedInSeconds = false, onCancel, onClear, onEnter }) {
    const [whole, initialFraction = ""] = (currentTime ?? "").split(".");
    const initialParts = whole.split(":");
    const [minutes, setMinutes] = useState(initialParts.length === 2 ? initialParts[0] : "");
    const [seconds, setSeconds] = useState(initialParts.length === 2 ? initialParts[1] : initialParts[0] ?? "");
    const [fraction, setFraction] = useState(initialFraction);
    const [error, setError] = useState("");
    function enterTime() {
        if (seconds === "")
            return setError("Enter seconds for this split.");
        const secondsValue = Number(seconds);
        const minutesValue = timedInSeconds || minutes === "" ? 0 : Number(minutes);
        if (minutesValue < 0 || minutesValue > 99)
            return setError("Minutes must be between 0 and 99.");
        if (secondsValue < 0 || secondsValue > 59)
            return setError("Seconds must be between 0 and 59.");
        const decimal = fraction || "00";
        const time = !timedInSeconds && minutesValue > 0 ? `${minutesValue}:${String(secondsValue).padStart(2, "0")}.${decimal}` : `${secondsValue}.${decimal}`;
        onEnter(time);
    }
    return _jsx("div", { className: "split-time-backdrop", role: "presentation", onMouseDown: onCancel, children: _jsxs("section", { className: "split-time-dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "split-time-title", onMouseDown: (event) => event.stopPropagation(), children: [_jsx("button", { className: "split-time-close", type: "button", onClick: onCancel, "aria-label": "Close", children: _jsx(X, { size: 19 }) }), _jsxs("header", { children: [_jsx("span", { children: _jsx(Timer, { size: 20 }) }), _jsxs("div", { children: [_jsxs("h3", { id: "split-time-title", children: ["Enter ", distance, "m Split"] }), _jsx("p", { children: "Enter the time for this section." })] })] }), _jsxs("div", { className: "split-time-inputs", children: [!timedInSeconds && _jsxs(_Fragment, { children: [_jsxs("label", { children: [_jsx("span", { children: "Minutes" }), _jsx("input", { autoFocus: true, inputMode: "numeric", maxLength: 2, placeholder: "0", value: minutes, onChange: (event) => { setMinutes(digits(event.target.value, 2)); setError(""); } })] }), _jsx("b", { children: ":" })] }), _jsxs("label", { children: [_jsx("span", { children: "Seconds" }), _jsx("input", { autoFocus: timedInSeconds, inputMode: "numeric", maxLength: 2, placeholder: "00", value: seconds, onChange: (event) => { setSeconds(digits(event.target.value, 2)); setError(""); } })] }), _jsx("b", { children: "." }), _jsxs("label", { children: [_jsx("span", { children: "Hundredths" }), _jsx("input", { inputMode: "numeric", maxLength: timedInSeconds ? 3 : 2, placeholder: "00", value: fraction, onChange: (event) => { setFraction(digits(event.target.value, timedInSeconds ? 3 : 2)); setError(""); } })] })] }), error && _jsx("div", { className: "meet-form-error", children: error }), _jsxs("footer", { children: [_jsx("button", { className: "split-time-clear", type: "button", onClick: onClear, children: "Clear" }), _jsx("button", { type: "button", onClick: onCancel, children: "Cancel" }), _jsx("button", { type: "button", onClick: enterTime, children: "Enter" })] })] }) });
}
